import React, { useState } from 'react';
import { SidebarLayout, PageHeader, Card } from '../components/layout';
import { useAuth } from '../hooks/use-auth';
import { useUserDivisions } from '../hooks/use-user-divisions';
import {
  useMyDelegationsGiven,
  useMyDelegationsReceived,
  useMyActiveDelegation,
  useCreateDelegation,
  useDeactivateDelegation,
  useDeleteDelegation,
} from '../hooks/use-delegations';
import { DelegationDto, CreateDelegationRequest } from '../types';
import { format, parseISO, isBefore, isAfter } from 'date-fns';
import { Calendar, UserCheck, UserX, Trash2, X, Plus, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { UserService } from '../services/user-service';
import httpClient from '../services/http-client';

const userService = new UserService(httpClient);

export function DelegationsPage() {
  const { user } = useAuth();
  const { data: userDivisionsData } = useUserDivisions();
  const divisions = userDivisionsData?.divisions || [];
  const [selectedDivisionId, setSelectedDivisionId] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'given' | 'received'>('given');

  // Set default division when data loads
  React.useEffect(() => {
    if (divisions && divisions.length > 0 && !selectedDivisionId) {
      setSelectedDivisionId(divisions[0].id);
    }
  }, [divisions, selectedDivisionId]);

  const { data: delegationsGiven, isLoading: isLoadingGiven } = useMyDelegationsGiven(selectedDivisionId);
  const { data: delegationsReceived, isLoading: isLoadingReceived } = useMyDelegationsReceived(selectedDivisionId);
  const { data: activeDelegation } = useMyActiveDelegation(selectedDivisionId);

  const createMutation = useCreateDelegation();
  const deactivateMutation = useDeactivateDelegation();
  const deleteMutation = useDeleteDelegation();

  const handleCreateDelegation = () => {
    setShowCreateModal(true);
  };

  const handleDeactivate = async (delegationId: string) => {
    if (!selectedDivisionId) return;
    
    if (confirm('Are you sure you want to deactivate this delegation?')) {
      await deactivateMutation.mutateAsync({ divisionId: selectedDivisionId, delegationId });
    }
  };

  const handleDelete = async (delegationId: string) => {
    if (!selectedDivisionId) return;
    
    if (confirm('Are you sure you want to delete this delegation? This action cannot be undone.')) {
      await deleteMutation.mutateAsync({ divisionId: selectedDivisionId, delegationId });
    }
  };

  const isLoading = activeTab === 'given' ? isLoadingGiven : isLoadingReceived;
  const delegations = activeTab === 'given' ? delegationsGiven?.delegations : delegationsReceived?.delegations;

  return (
    <SidebarLayout>
      <div className="space-y-6">
        <PageHeader
          title="Approval Delegations"
          subtitle="Manage temporary delegation of your approval authority to other users"
        />

        {/* Active Delegation Banner */}
        {activeDelegation && (
          <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <UserCheck className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-900">Active Delegation</h3>
                <p className="text-sm text-blue-700 mt-1">
                  You have delegated your approval authority to <strong>{activeDelegation.delegateName}</strong>
                  {' '}from {format(parseISO(activeDelegation.startDate), 'MMM d, yyyy')}
                  {' '}to {format(parseISO(activeDelegation.endDate), 'MMM d, yyyy')}
                </p>
                {activeDelegation.reason && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Reason: {activeDelegation.reason}</p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Division Selector */}
        {divisions && divisions.length > 1 && (
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-secondary-200">Division:</label>
            <select
              value={selectedDivisionId}
              onChange={(e) => setSelectedDivisionId(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
            >
              {divisions.map((div) => (
                <option key={div.id} value={div.id}>
                  {div.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Tabs and Actions */}
        <div className="flex items-center justify-between">
          <div className="border-b border-gray-200 dark:border-secondary-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('given')}
                data-testid="tab-given"
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'given'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 hover:border-gray-300 dark:hover:border-secondary-500'
                }`}
              >
                Delegations Given
                {delegationsGiven && delegationsGiven.delegations.length > 0 && (
                  <span className="ml-2 bg-gray-100 dark:bg-secondary-700 text-gray-900 dark:text-white py-0.5 px-2 rounded-full text-xs">
                    {delegationsGiven.delegations.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('received')}
                data-testid="tab-received"
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'received'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 hover:border-gray-300 dark:hover:border-secondary-500'
                }`}
              >
                Delegations Received
                {delegationsReceived && delegationsReceived.delegations.length > 0 && (
                  <span className="ml-2 bg-gray-100 dark:bg-secondary-700 text-gray-900 dark:text-white py-0.5 px-2 rounded-full text-xs">
                    {delegationsReceived.delegations.length}
                  </span>
                )}
              </button>
            </nav>
          </div>

          {activeTab === 'given' && (
            <button
              onClick={handleCreateDelegation}
              data-testid="create-delegation-btn"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Delegation
            </button>
          )}
        </div>

        {/* Delegations List */}
        {isLoading ? (
          <Card>
            <div className="text-center py-12 text-gray-500 dark:text-secondary-400">Loading delegations...</div>
          </Card>
        ) : !delegations || delegations.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <UserX className="h-12 w-12 text-gray-400 dark:text-secondary-500 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-secondary-400">
                {activeTab === 'given' 
                  ? 'No delegations given yet'
                  : 'No delegations received yet'}
              </p>
              {activeTab === 'given' && (
                <button
                  onClick={handleCreateDelegation}
                  className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-700"
                >
                  Create your first delegation
                </button>
              )}
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {delegations.map((delegation) => (
              <DelegationCard
                key={delegation.id}
                delegation={delegation}
                type={activeTab}
                onDeactivate={handleDeactivate}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* Create Delegation Modal */}
        {showCreateModal && selectedDivisionId && (
          <CreateDelegationModal
            divisionId={selectedDivisionId}
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
            }}
          />
        )}
      </div>
    </SidebarLayout>
  );
}

interface DelegationCardProps {
  delegation: DelegationDto;
  type: 'given' | 'received';
  onDeactivate: (id: string) => void;
  onDelete: (id: string) => void;
}

function DelegationCard({ delegation, type, onDeactivate, onDelete }: DelegationCardProps) {
  const now = new Date();
  const startDate = parseISO(delegation.startDate);
  const endDate = parseISO(delegation.endDate);
  
  const isActive = delegation.active && delegation.isCurrentlyActive;
  const isPending = delegation.active && isBefore(now, startDate);
  const isExpired = isAfter(now, endDate) || !delegation.active;

  const statusColor = isActive ? 'bg-green-100 dark:bg-green-900 text-green-800' : 
                      isPending ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800' : 
                      'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-100';
  
  const statusText = isActive ? 'Active' : isPending ? 'Pending' : 'Inactive';

  return (
    <div data-testid={`delegation-card-${delegation.id}`}>
    <Card className="hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
              {statusText}
            </span>
            {type === 'given' ? (
              <div className="text-sm">
                <span className="text-gray-600 dark:text-secondary-400">Delegated to:</span>
                <span className="font-medium text-gray-900 dark:text-white ml-1">{delegation.delegateName}</span>
                <span className="text-gray-500 dark:text-secondary-400 ml-1">({delegation.delegateEmail})</span>
              </div>
            ) : (
              <div className="text-sm">
                <span className="text-gray-600 dark:text-secondary-400">From:</span>
                <span className="font-medium text-gray-900 dark:text-white ml-1">{delegation.delegatorName}</span>
                <span className="text-gray-500 dark:text-secondary-400 ml-1">({delegation.delegatorEmail})</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-secondary-400 mt-3">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>
                {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-secondary-400">
              {delegation.divisionName}
            </div>
          </div>

          {delegation.reason && (
            <div className="mt-2 text-sm text-gray-600 dark:text-secondary-400">
              <span className="font-medium">Reason:</span> {delegation.reason}
            </div>
          )}
        </div>

        {type === 'given' && (
          <div className="flex items-center gap-2 ml-4">
            {isActive && (
              <button
                onClick={() => onDeactivate(delegation.id)}
                className="p-2 text-orange-600 dark:text-orange-400 hover:bg-orange-50 rounded-md transition-colors"
                title="Deactivate"
                data-testid={`deactivate-btn-${delegation.id}`}
              >
                <UserX className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={() => onDelete(delegation.id)}
              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 rounded-md transition-colors"
              title="Delete"
              data-testid={`delete-btn-${delegation.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </Card>
    </div>
  );
}

interface CreateDelegationModalProps {
  divisionId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function CreateDelegationModal({ divisionId, onClose, onSuccess }: CreateDelegationModalProps) {
  const [delegateUserId, setDelegateUserId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const createMutation = useCreateDelegation();

  // Fetch users in the division
  const { data: usersData } = useQuery({
    queryKey: ['division-users', divisionId],
    queryFn: async () => {
      const response = await httpClient.get<any>(`/api/v1/divisions/${divisionId}/users`);
      return response;
    },
    enabled: !!divisionId,
  });

  const users = usersData?.users || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!delegateUserId || !startDate || !endDate) {
      setError('Please fill in all required fields');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start >= end) {
      setError('End date must be after start date');
      return;
    }

    if (end < new Date()) {
      setError('End date must be in the future');
      return;
    }

    const request: CreateDelegationRequest = {
      delegateUserId,
      divisionId,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      reason: reason || undefined,
    };

    try {
      await createMutation.mutateAsync({ divisionId, request });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create delegation');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-secondary-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create Delegation</h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
              Delegate To <span className="text-red-500">*</span>
            </label>
            <select
              value={delegateUserId}
              onChange={(e) => setDelegateUserId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              required
            >
              <option value="">Select a user...</option>
              {users.map((user: any) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
              Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
              End Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
              Reason (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              placeholder="e.g., Vacation, Out of office, etc."
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-secondary-200 hover:bg-gray-100 dark:hover:bg-secondary-600 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Delegation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
